import React, { Fragment, useState } from 'react';
import RMultilingual from '../Multilingual';
import toast from '~~elements/Toast';
import Input from '~~elements/Input';
import Form from '~~elements/Form';
import Button from '~~elements/Button';
import { SeasonRegistryResource } from '~~apis/resource';
import { RegistrySectionDiv } from '../../styled';
import { useRegistryDispatch, useRegistryState } from '../useRegistry';
import { findIndex } from 'ramda';

const DocumentSection = () => {
  const { registryInfo } = useRegistryState();
  const registryDispatch = useRegistryDispatch();

  const [displayRemoveHint, setDisplayRemoveHint] = useState(-1);

  const [title, setTitle] = useState('');
  const [contentRefresh, setAttachmentRefresh] = useState(0);
  const [uploadPendingFile, setUploadPendingFile] = useState(null);

  const [uploading, setUploading] = useState(false);

  const removeAttachment = (index) => () => {
    if (index !== displayRemoveHint) {
      setDisplayRemoveHint(index);
      return;
    }

    setUploading(true);
    SeasonRegistryResource.removeDocument({
      seasonRegistryUniqid: registryInfo.uniqid,
      documentIndex: displayRemoveHint,
    })
      .then(({ data: newAttachments }) => {
        registryDispatch({ type: 'UPDATE_ATTACHMENTS', payload: newAttachments });
        toast(RMultilingual(`SUCCESS.DELETE`), { status: 'success', second: 2 });
        setDisplayRemoveHint(-1);
      })
      .catch((e) => {
        toast(RMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const handleTitleOnChange = (e) => {
    setTitle(e.target.value);
  };

  const handleFileOnChange = (e) => {
    e.preventDefault();

    const file = e.target.files[0];
    if (!file) {
      setUploadPendingFile(null);
      return;
    }

    const { size: fileSize, type } = file;

    if (type !== 'application/pdf' || fileSize > 1024 * 1024 * 25) {
      toast(RMultilingual(`ERROR.DOCUMENT`), { status: 'error', second: 2 });
      setAttachmentRefresh((pre) => pre + 1);
      setUploadPendingFile(null);
      return;
    }

    setUploadPendingFile(file);
  };

  const handleAttachmentUpload = () => {
    const attachmentTitle = title.trim().replace(/[//\\?%*:|"<>]/g, '');
    const sameTitleIndex = findIndex((a) => a.title === attachmentTitle, registryInfo.attachments);

    if (sameTitleIndex >= 0) {
      toast(RMultilingual(`DOCUMENT_DUPLICATE_TITLE_HINT`));
    }

    setUploading(true);
    SeasonRegistryResource.uploadDocument({
      seasonRegistryUniqid: registryInfo.uniqid,
      replaceIndex: sameTitleIndex,
      title: attachmentTitle,
      file: uploadPendingFile,
    })
      .then(({ data: newAttachments }) => {
        registryDispatch({ type: 'UPDATE_ATTACHMENTS', payload: newAttachments });
        toast(RMultilingual(`SUCCESS.UPLOAD`), { status: 'success', second: 2 });
        setTitle('');
        setAttachmentRefresh((pre) => pre + 1);
      })
      .catch((e) => {
        toast(RMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
      })
      .finally(() => {
        setUploading(false);
      });
  };

  return (
    <RegistrySectionDiv>
      <Form onSubmit={(e) => e.preventDefault()}>
        <div className="box">
          {registryInfo.attachments.map((attachment, index) => (
            <Fragment key={index}>
              <span>
                {attachment.title}
                {displayRemoveHint === index ? (
                  <Fragment>
                    <Button
                      width="unset"
                      mini
                      color="red"
                      onClick={removeAttachment(index)}
                      type="button"
                      disabled={uploading}
                    >
                      {RMultilingual(`COMMON.DELETE_CONFIRM`)}
                    </Button>
                    <Button
                      width="unset"
                      mini
                      color="transparent"
                      onClick={removeAttachment(-1)}
                      type="button"
                      disabled={uploading}
                    >
                      {RMultilingual(`COMMON.CANCEL`)}
                    </Button>
                  </Fragment>
                ) : (
                  <Button
                    width="unset"
                    color="transparent"
                    onClick={removeAttachment(index)}
                    type="button"
                    disabled={uploading}
                  >
                    X
                  </Button>
                )}
              </span>
              <div className="input-field">
                <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                  {attachment.url}
                </a>
              </div>
            </Fragment>
          ))}
          <div className="input-field" />
          <label>{RMultilingual(`DOCUMENT_TITLE`)}</label>
          <div className="input-field">
            <Input width="10rem" value={title} onChange={handleTitleOnChange} disabled={uploading} />
          </div>
          <div className="input-field">
            <input
              key={contentRefresh}
              type="file"
              accept="application/pdf"
              onChange={handleFileOnChange}
              disabled={uploading}
            />
          </div>
          <Button
            width="unset"
            color="red"
            disabled={!title.trim() || !uploadPendingFile || uploading}
            onClick={handleAttachmentUpload}
            type="button"
          >
            {RMultilingual(`FORM.UPLOAD`)}
          </Button>
        </div>
      </Form>
    </RegistrySectionDiv>
  );
};

export default DocumentSection;
